import { quantidadeContratosObs } from "../../desconto-grupo.js";
import { lerDinheiro, montarLinhaDelimitada } from "../../csv-delimitado.js";
import { COLUNAS_CONSIGNADOS, RETORNO_SEPARADOR } from "../../retorno.js";
import { COLUNAS_RUBRICA, normalizarCodigoVerba } from "../../rubrica.js";
import { layoutDoCatalogo } from "../catalogo.js";
import { acharPorTrecho } from "../reconhecer.js";
import { apenasDigitos, cpfCompletado } from "../identidade.js";
import { adaptadorAleceRetorno, dataAleceParaDDMMAAAA } from "./alece-retorno.js";
import { semZerosAEsquerda } from "../../margem-informada.js";
import { colunasFaltando, conferirCabecalhoAlece, filtrarTabela, lerTabelaAlece, normalizarNomeColuna } from "./alece-tabela.js";
import { COLUNAS_CONTRATOS_ALECE } from "./alece-carga-colunas.js";
import { conferirFormulaMargemAlece } from "./alece-conferencia.js";
export { COLUNAS_CONTRATOS_ALECE } from "./alece-carga-colunas.js";
const CODIGO = "alece-carga-inicial";
/**
 * A versao SAI DO CATALOGO, nunca de uma constante daqui.
 *
 * Ate 16/09/2026 este arquivo repetia `"1.2"` a mao, o catalogo dizia `"1.2"` em
 * outro lugar e as DUAS rotas que gravam o registro do arquivo trocado
 * escreviam `"1.0"` — tres verdades para um numero so'. `alece-retorno` ja
 * resolvia pelo catalogo; a carga passa a fazer igual, e a proxima subida de
 * versao acontece num lugar unico.
 */
function versao() {
    return layoutDoCatalogo(CODIGO).versao;
}
const OBRIGATORIAS = COLUNAS_CONTRATOS_ALECE.map(normalizarNomeColuna);
/**
 * Milhar com VIRGULA e decimal com PONTO: `1,696.87`, `12,345`.
 *
 * `1.696,87` (a forma brasileira) NAO casa — o ponto vem antes da virgula — e
 * `3000,00`, `120,00` e `1696.87` tambem nao: os dois primeiros tem 2 digitos
 * depois da virgula, o terceiro nao tem virgula.
 */
const MILHAR_COM_VIRGULA = /^-?\d{1,3}(?:,\d{3})+(?:\.\d{1,2})?$/;
/**
 * Dinheiro em centavos, com UMA recusa a mais que `lerDinheiro`.
 *
 * `lerDinheiro` desempata `,` e `.` pela regra do repositorio: havendo virgula,
 * ela e' o decimal. Isso acerta as duas formas que a ALECE manda de verdade —
 * `"1696,87"` entre aspas no CSV e `16.97` na planilha — e erra `1,696.87`, que
 * sairia como R$ 1,70: sem excecao, sem descarte, sem linha no relatorio.
 *
 * Nenhum arquivo da ALECE usa essa forma hoje. A guarda existe porque os dois
 * custos sao assimetricos: estar errado e' averbar uma parcela mil vezes menor
 * e so' descobrir na folha; recusar e' uma pendencia que alguem le. `null` cai
 * em "valores invalidos" no contrato e tira a linha das margens.
 *
 * NAO se conserta isso em `lerDinheiro`: ela serve todos os layouts, e a
 * ambiguidade so' e' decidivel sabendo de quem e' o arquivo (ADR-0007).
 */
function cents(v) {
    const t = v.trim().replace(/R\$\s*/i, "").replace(/\s/g, "");
    if (MILHAR_COM_VIRGULA.test(t))
        return null;
    const n = lerDinheiro(v);
    return n == null || !Number.isFinite(n) ? null : Math.round(n * 100);
}
function cpf(v) {
    return cpfCompletado(v)?.cpf ?? apenasDigitos(v);
}
function inteiro(v) {
    const t = v.trim();
    if (!/^\d+$/.test(t))
        return null;
    return Number(t);
}
function isoData(v) {
    const d = dataAleceParaDDMMAAAA(v);
    return d ? `${d.slice(4)}-${d.slice(2, 4)}-${d.slice(0, 2)}` : null;
}
const MESES = {
    JANEIRO: "01",
    FEVEREIRO: "02",
    MARCO: "03",
    ABRIL: "04",
    MAIO: "05",
    JUNHO: "06",
    JULHO: "07",
    AGOSTO: "08",
    SETEMBRO: "09",
    OUTUBRO: "10",
    NOVEMBRO: "11",
    DEZEMBRO: "12",
};
function semAcento(v) {
    return v
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toUpperCase();
}
function competencia(v) {
    const t = semAcento(v);
    const direto = apenasDigitos(t);
    if (/^\d{6}$/.test(direto))
        return direto;
    const m = /([A-Z]+)\s+DE\s+(\d{4})/.exec(t);
    return m && MESES[m[1]] ? `${m[2]}${MESES[m[1]]}` : null;
}
function produto(v) {
    const t = nomeModalidade(v);
    if (t === "EMPRESTIMO CONSIGNADO")
        return "EMPRESTIMO";
    if (t === "CARTAO CONSIGNADO")
        return "CARTAO_CONSIGNADO";
    if (t === "CARTAO BENEFICIO" || t === "CARTAO DE BENEFICIO")
        return "CARTAO_BENEFICIOS";
    return null;
}
function nomeModalidade(v) {
    return semAcento(v).replace(/\s*\(\d+\)\s*$/, "");
}
const MODALIDADES_NAO_CONSIGNAVEIS = new Set([
    "MENSALIDADE",
    "PLANO DE SAUDE",
    "PLANO ODONTOLOGICO",
    "SEGUROS",
    "ADIANTAMENTO",
    "COMPRAS DIVERSAS",
    "DIFERENCAS",
]);
function modalidadeNaoConsignavel(v) {
    return MODALIDADES_NAO_CONSIGNAVEIS.has(nomeModalidade(v));
}
function naoConsignavelReconhecida(c) {
    return c.naturezaRubrica === "desconto_nao_consignado" && c.verba.trim() !== "";
}
/**
 * Nome de cada rubrica como a FOLHA da ALECE escreve: `RUB` -> `DESCRUB`.
 *
 * O nome da rubrica no catalogo vem DAQUI, e nunca da `Modalidade` da planilha
 * CONTRATOS. Modalidade e' classificacao ("Empréstimo Consignado", "Plano de
 * Saúde"), serve para produto e natureza; o nome e' o que a folha da ALECE usa
 * (`BRADESCO EMPRESTIMO`, `UNIMED`) — e e' ele que o movimento devolve no
 * DESCRUB. Ate 23/09/2026 a carga gravava a modalidade, e o movimento mandava
 * para a ALECE um texto que nao era o dela.
 *
 * Primeiro DESCRUB nao vazio de cada verba, linhas C e N.
 */
export function descricoesDaFolhaAlece(folha) {
    const m = new Map();
    for (const f of folha) {
        const verba = normalizarCodigoVerba(f.rubrica);
        if (verba === "" || f.descricao === "" || m.has(verba))
            continue;
        m.set(verba, f.descricao);
    }
    return m;
}
/**
 * Arquivo canonico de RUBRICA da carga inicial.
 *
 * Duas fontes, e a divisao e' deliberada:
 *
 *   - as verbas dos CONTRATOS saem uma linha por contrato, com o valor da
 *     parcela — como antes. Este texto tambem e' o holerite que confirma cada
 *     linha do consignados (multiset matricula|verba|centavos), e a FOLHA vem
 *     AGREGADA por (FF, MATR, RUB): trocar por ela deixaria parcela sem
 *     confirmacao. Muda so' o nome: DESCRUB, ou vazio quando a verba nao veio
 *     na FOLHA — o primeiro retorno mensal que a trouxer preenche;
 *   - as verbas que so' a FOLHA traz entram pelas linhas dela, como no retorno
 *     mensal. Nenhuma linha do consignados disputa essas.
 */
export function linhasRubricaCargaAlece(args) {
    const descricoes = descricoesDaFolhaAlece(args.folha);
    const cobertas = new Set(args.contratos.map((c) => normalizarCodigoVerba(c.verba)));
    const linhas = args.contratos.map((c) => montarLinhaDelimitada(COLUNAS_RUBRICA, {
        competencia: args.competencia,
        matricula: c.matricula,
        codigoVerba: c.verba,
        descricao: descricoes.get(normalizarCodigoVerba(c.verba)) ?? "",
        tipo: "D",
        valor: (c.valorParcelaCentavos ?? 0) / 100,
    }, RETORNO_SEPARADOR));
    for (const f of args.folha) {
        const verba = normalizarCodigoVerba(f.rubrica);
        if (f.matricula === "" || verba === "" || cobertas.has(verba))
            continue;
        linhas.push(montarLinhaDelimitada(COLUNAS_RUBRICA, {
            competencia: args.competencia,
            matricula: f.matricula,
            codigoVerba: f.rubrica,
            descricao: descricoes.get(verba) ?? "",
            tipo: "D",
            valor: f.pagoCentavos / 100,
        }, RETORNO_SEPARADOR));
    }
    return linhas;
}
/**
 * Arquivo canonico de CONSIGNADOS da carga inicial: uma linha por contrato.
 *
 * A descricao e' o DESCRUB da verba, como na rubrica. Sem DESCRUB, o
 * consignatario da planilha — o campo e' lido como consignataria quando a verba
 * cai em conciliacao, e ali vazio nao diz nada a quem vai resolver.
 */
export function linhasConsignadosCargaAlece(args) {
    const descricoes = descricoesDaFolhaAlece(args.folha);
    return args.contratos.map((c) => montarLinhaDelimitada(COLUNAS_CONSIGNADOS, {
        matricula: c.matricula,
        codigoVerba: c.verba,
        descricao: descricoes.get(normalizarCodigoVerba(c.verba)) || c.consignatarioOriginal,
        tipo: "D",
        valor: (c.valorParcelaCentavos ?? 0) / 100,
        parcelasRestantes: c.prazoRestante ?? 0,
        referencia: c.codigo,
        parcelaAtual: "",
        parcelasTotal: c.prazoTotal ?? 0,
    }, RETORNO_SEPARADOR));
}
function tabelaPlanilha(a) {
    const linhas = a.celulas ?? [];
    if (linhas.length === 0)
        return { cabecalho: [], registros: [], linhas: [] };
    const cabecalho = linhas[0].map(normalizarNomeColuna);
    const registros = [];
    const nums = [];
    for (let i = 1; i < linhas.length; i++) {
        const row = linhas[i];
        if (row.every((x) => String(x ?? "").trim() === ""))
            continue;
        const r = {};
        cabecalho.forEach((h, j) => {
            r[h] = String(row[j] ?? "").trim();
        });
        registros.push(r);
        nums.push(i + 1);
    }
    return { cabecalho, registros, linhas: nums };
}
function repetidos(xs) {
    const seen = new Set(), dup = new Set();
    xs.forEach((x) => {
        if (seen.has(x))
            dup.add(x);
        else
            seen.add(x);
    });
    return [...dup];
}
function arquivo(arquivos, trecho) {
    return acharPorTrecho(arquivos, trecho);
}
export const adaptadorAleceCargaInicial = {
    codigo: CODIGO,
    reconhecer(arquivos) {
        const servidor = arquivo(arquivos, "servidor"), folha = arquivo(arquivos, "folha"), contratos = arquivo(arquivos, "contratos");
        const encontrados = [servidor?.nome, folha?.nome, contratos?.nome].filter(Boolean);
        const faltando = [];
        if (!servidor)
            faltando.push("servidor");
        if (!folha)
            faltando.push("folha");
        if (!contratos)
            faltando.push("contratos");
        if (faltando.length)
            return {
                reconhecido: false,
                layout: CODIGO,
                encontrados,
                faltando,
                motivo: `a carga inicial da ALECE precisa conter ${faltando.join(", ")}`,
            };
        // SERVIDOR e FOLHA da carga inicial sao `.CSV`; os do retorno mensal
        // continuam `.TXT`. Ambos detectam o delimitador pelo cabecalho. A diferenca
        // mora SO' na extensao: e' ela que diz por qual porta o pacote entrou, e
        // aceitar as duas em qualquer uma apagaria o unico sinal que separa um
        // tombamento de um ciclo mensal.
        const nomesValidos = [
            [servidor.nome, /^ALECE-SERVIDOR-\d{4}\.CSV$/i],
            [folha.nome, /^ALECE-FOLHA-\d{4}\.CSV$/i],
            [contratos.nome, /^ALECE-CONTRATOS-\d{4}\.XLSX$/i],
        ];
        const nomesInvalidos = nomesValidos
            .filter(([nome, re]) => !re.test(nome.replace(/^.*[\\/]/, "")))
            .map(([nome]) => nome);
        if (nomesInvalidos.length) {
            // `.TXT` nao e' um nome errado qualquer: e' o nome CERTO do retorno
            // mensal, e quem manda assim acertou o arquivo e errou a rota. A mensagem
            // generica mandaria conferir o padrao do nome — que o operador cumpriu.
            // Esta diz a unica coisa acionavel.
            const comoRetorno = nomesInvalidos.filter((n) => /^ALECE-(?:SERVIDOR|FOLHA)-\d{4}\.TXT$/i.test(n.replace(/^.*[\\/]/, "")));
            return {
                reconhecido: false,
                layout: CODIGO,
                encontrados,
                faltando: nomesInvalidos,
                motivo: comoRetorno.length
                    ? `a carga inicial da ALECE lê SERVIDOR e FOLHA em .CSV; ${comoRetorno.join(", ")} veio em .TXT, que é o formato do retorno mensal`
                    : `nomes fora do padrão da carga inicial ALECE: ${nomesInvalidos.join(", ")}`,
            };
        }
        if (contratos.origem !== "planilha")
            return {
                reconhecido: false,
                layout: CODIGO,
                encontrados,
                faltando: ["contratos.xlsx"],
                motivo: "ALECE-CONTRATOS precisa ser uma planilha XLSX",
            };
        // Os tres arquivos conferidos ANTES de responder (so' o cabecalho): o que
        // falta em servidor, folha e contratos sai numa lista, como na `main`.
        const cs = conferirCabecalhoAlece(servidor.texto, "servidor");
        const cf = conferirCabecalhoAlece(folha.texto, "folha");
        const cp = tabelaPlanilha(contratos).cabecalho;
        const aus = [
            ...cs.faltando.map((x) => `servidor.${x}`),
            ...cf.faltando.map((x) => `folha.${x}`),
            ...colunasFaltando(cp, OBRIGATORIAS).map((x) => `contratos.${x}`),
        ];
        const dup = repetidos(cp);
        const motivosSeparador = [cs.motivo, cf.motivo].filter((m) => !!m);
        if (motivosSeparador.length || aus.length || dup.length)
            return {
                reconhecido: false,
                layout: CODIGO,
                encontrados,
                faltando: aus,
                motivo: [
                    ...motivosSeparador,
                    aus.length ? `cabeçalhos ausentes: ${aus.join(", ")}` : "",
                    dup.length ? `cabeçalhos duplicados: ${dup.join(", ")}` : "",
                ]
                    .filter(Boolean)
                    .join("; "),
            };
        return { reconhecido: true, layout: CODIGO, encontrados, faltando: [] };
    },
    traduzir(arquivos, ctx) {
        const servidor = arquivo(arquivos, "servidor"), folha = arquivo(arquivos, "folha"), planilha = arquivo(arquivos, "contratos");
        const aammEsperado = ctx.competencia.slice(2);
        for (const a of [servidor, folha, planilha]) {
            const aamm = /-([0-9]{4})\.(?:CSV|XLSX)$/i.exec(a.nome.replace(/^.*[\\/]/, ""))?.[1];
            if (aamm !== aammEsperado)
                throw new Error(`${a.nome}: competência do nome (${aamm ?? "ausente"}) diverge de ${ctx.competencia}`);
        }
        // DIVIDA CONHECIDA (revisao de 28/09/2026): a traducao base ja' leu e
        // normalizou SERVIDOR e FOLHA, e as duas linhas abaixo leem de novo para
        // filtrar pelo que a base aceitou — dois parses por arquivo dentro do
        // request. Ler uma vez exigiria a traducao base expor as tabelas lidas;
        // fica para quando o tombamento voltar a ser tocado.
        const base = adaptadorAleceRetorno.traduzir([servidor, folha], ctx);
        const linhasServidor = new Set(base.pacote.margensInformadas.map((m) => m.linha));
        const linhasFolha = new Set(base.pacote.folhaLinhas.map((f) => f.linha));
        const ts = filtrarTabela(lerTabelaAlece(servidor.texto, "servidor"), (_, linha) => linhasServidor.has(linha));
        const tf = filtrarTabela(lerTabelaAlece(folha.texto, "folha"), (_, linha) => linhasFolha.has(linha));
        const tc = tabelaPlanilha(planilha);
        const margens = [];
        ts.registros.forEach((r, i) => {
            const m40 = cents(r.MARGEM40 ?? ""), m60 = cents(r.MARGEM60 ?? "");
            {
                const usa60 = (r.M60 ?? "").trim().toUpperCase() === "S";
                const corte = (r.CORTE ?? "").trim().toUpperCase() === "S";
                const inconsistencias = [];
                if ((usa60 || corte) && m40 !== null && m60 !== null && m40 !== m60) {
                    inconsistencias.push("MARGEM40 e MARGEM60 deveriam ser iguais quando M60=S ou CORTE=S");
                }
                inconsistencias.push(...conferirFormulaMargemAlece({
                    salBaseCentavos: cents(r.SALBASE ?? ""),
                    margemBrutaCentavos: cents(r.MARGBRUTA ?? ""),
                    descontoObrigatorioCentavos: cents(r.DESCOBRIG ?? ""),
                    outrosDescontosCentavos: cents(r.OUTROSDESC ?? ""),
                    descontoRealCentavos: cents(r.DESCREAL ?? ""),
                    saldoCentavos: usa60 ? m60 : m40,
                    m60: usa60,
                    corte,
                }));
                margens.push({
                    linha: ts.linhasDosRegistros[i],
                    matricula: (r.MATR ?? "").trim(),
                    cpf: cpf(r.CPF ?? ""),
                    competencia: apenasDigitos(r.ANOMES ?? ""),
                    margem40Centavos: m40,
                    margem60Centavos: m60,
                    saldoDisponivelCentavos: usa60 ? m60 : m40,
                    m60: usa60,
                    corte,
                    bloqueado: (r.BLOQ ?? "").trim().toUpperCase() === "S",
                    margemBrutaCentavos: cents(r.MARGBRUTA ?? ""),
                    descontoObrigatorioCentavos: cents(r.DESCOBRIG ?? ""),
                    outrosDescontosCentavos: cents(r.OUTROSDESC ?? ""),
                    descontoRealCentavos: cents(r.DESCREAL ?? ""),
                    folha: (r.FF ?? "").trim(),
                    regime: (r.REGIME ?? "").trim(),
                    inconsistencias,
                });
            }
        });
        const servidores = new Map(ts.registros.map((r) => [
            (r.MATR ?? "").trim(),
            { cpf: cpf(r.CPF ?? ""), nome: (r.NOME ?? "").trim() },
        ]));
        const folhasPorMatricula = new Map(ts.registros.map((r) => [(r.MATR ?? "").trim(), (r.FF ?? "").trim()]));
        for (const r of tf.registros) {
            const matricula = (r.MATR ?? "").trim();
            const ffServidor = folhasPorMatricula.get(matricula);
            if (ffServidor != null && ffServidor !== (r.FF ?? "").trim()) {
                throw new Error(`ALECE-FOLHA: FF divergente do ALECE-SERVIDOR para a matrícula ${matricula}`);
            }
        }
        const codigos = new Map();
        tc.registros.forEach((r) => {
            const c = r[normalizarNomeColuna("Código")] ?? "";
            codigos.set(c, (codigos.get(c) ?? 0) + 1);
        });
        const contratos = tc.registros.map((r, i) => {
            const g = (nome) => r[normalizarNomeColuna(nome)] ?? "";
            const codigo = g("Código").trim(), pt = inteiro(g("Prazo Total")), pr = inteiro(g("Prazo Restante")), vp = cents(g("Valor da Parcela")), vt = cents(g("Total Parcelas"));
            const p = produto(g("Modalidade"));
            const naoConsignavel = modalidadeNaoConsignavel(g("Modalidade"));
            const periodo = competencia(g("Período"));
            const verba = g("Verba").trim();
            const motivos = [];
            const sit = semAcento(g("Situação do contrato"));
            if (!codigo)
                motivos.push("código vazio");
            else if ((codigos.get(codigo) ?? 0) > 1)
                motivos.push("código repetido no arquivo");
            if (!p && !naoConsignavel)
                motivos.push("modalidade desconhecida");
            // O período pertence ao contrato e pode ser anterior ou posterior ao mês
            // do pacote. O pacote continua validado pelo AAMM dos três arquivos.
            if (!periodo)
                motivos.push("período inválido");
            if (pt === null || pr === null || pr > pt)
                motivos.push("prazos inválidos");
            if (vp === null || vp < 0 || vt === null)
                motivos.push("valores inválidos");
            else if (pt !== null && vp * pt !== vt)
                motivos.push("total de parcelas diverge de parcela × prazo");
            const servidorDaLinha = servidores.get(semZerosAEsquerda(g("Matrícula")));
            if (!servidorDaLinha)
                motivos.push("servidor ausente do ALECE-SERVIDOR");
            else if (servidorDaLinha.cpf !== cpf(g("CPF")))
                motivos.push("CPF divergente do ALECE-SERVIDOR");
            const car = /^\s*(\d+)\s*\/\s*(.+)$/.exec(g("Carência / Primeiro Lançamento"));
            return {
                linha: tc.linhas[i],
                codigo,
                codigoInstituicao: g("Código na instituição"),
                modalidade: g("Modalidade"),
                produto: p,
                naturezaRubrica: p ? "consignacao" : naoConsignavel ? "desconto_nao_consignado" : null,
                dataContrato: isoData(g("Data")),
                consignatarioOriginal: g("Consignatário"),
                verba,
                // A planilha de contratos segue a mesma identidade do SERVIDOR e da FOLHA:
                // sem zeros a esquerda, ou o contrato nao casa com o servidor.
                matricula: semZerosAEsquerda(g("Matrícula")),
                cpf: cpf(g("CPF")),
                folha: semZerosAEsquerda(g("Folha")),
                nome: g("Nome"),
                competencia: periodo,
                prazoTotal: pt,
                prazoRestante: pr,
                valorParcelaCentavos: vp,
                valorTotalCentavos: vt,
                valorLiberadoCentavos: cents(g("Valor liberado")),
                situacao: g("Situação do contrato"),
                primeiraParcela: car ? inteiro(car[1]) : null,
                primeiroLancamento: car ? competencia(car[2]) : null,
                terminoPrevisto: competencia(g("Previsão de Termino")),
                natureza: g("Natureza"),
                status: sit !== "ATIVO"
                    ? "ignorado"
                    : naoConsignavel
                        ? verba
                            ? "ignorado"
                            : "pendente"
                        : motivos.length
                            ? "pendente"
                            : "aceito",
                motivos: sit !== "ATIVO"
                    ? ["situação diferente de Ativo"]
                    : naoConsignavel
                        ? verba
                            ? ["modalidade classificada como desconto não consignável"]
                            : ["verba vazia"]
                        : motivos,
            };
        });
        const aceitos = contratos.filter((c) => c.status === "aceito");
        const conf = tf.registros.map((r, i) => ({
            linha: tf.linhasDosRegistros[i],
            codfp: (r.CODFP ?? "").trim(),
            ff: (r.FF ?? "").trim(),
            matricula: (r.MATR ?? "").trim(),
            cpf: cpf(r.CPF ?? ""),
            tipo: (r.TIPO ?? "").trim().toUpperCase(),
            rubrica: (r.RUB ?? "").trim(),
            descricao: (r.DESCRUB ?? "").trim(),
            observacao: (r.OBS ?? "").trim(),
            quantidadeContratos: quantidadeContratosObs(r.OBS ?? ""),
            valorCentavos: cents(r.VALOR ?? "") ?? 0,
            pagoCentavos: cents(r.PAGOFP ?? "") ?? 0,
        }));
        const rubricas = linhasRubricaCargaAlece({
            competencia: ctx.competencia,
            contratos: contratos.filter((c) => c.status === "aceito" || naoConsignavelReconhecida(c)),
            folha: conf,
        });
        const consignados = linhasConsignadosCargaAlece({ contratos: aceitos, folha: conf });
        base.pacote.consignados = consignados.join("\r\n") + (consignados.length ? "\r\n" : "");
        base.pacote.rubricas = rubricas.join("\r\n") + (rubricas.length ? "\r\n" : "");
        base.pacote.gruposDesconto = [];
        base.relatorio.layout = { codigo: CODIGO, versao: versao() };
        // No retorno mensal, PAGOFP=0 é descartado do arquivo canônico. Nesta
        // carga a mesma linha continua aproveitada como conferência e não pode ser
        // reportada como perda.
        base.relatorio.descartadas = base.relatorio.descartadas.filter((d) => d.arquivo !== "folha" || !d.motivo.startsWith("consignação não descontada"));
        base.relatorio.naoInformaveis = [];
        base.relatorio.conferidas = [{ arquivo: "folha", linhas: tf.registros.length }];
        base.relatorio.gerados = [
            base.relatorio.gerados.find((g) => g.arquivo === "carga"),
            { arquivo: "consignados", linhas: consignados.length },
            { arquivo: "rubrica", linhas: rubricas.length },
        ];
        base.relatorio.lidos.push({ arquivo: "contratos", linhas: contratos.length });
        contratos
            .filter((c) => c.status !== "aceito")
            .forEach((c) => base.relatorio.descartadas.push({
            arquivo: "contratos",
            linha: c.linha,
            matricula: c.matricula,
            motivo: c.motivos.join("; "),
        }));
        return {
            ...base,
            cargaInicialAlece: {
                arquivos: { servidor: servidor.nome, folha: folha.nome, contratos: planilha.nome },
                margens,
                contratos,
                folha: conf,
            },
        };
    },
};
//# sourceMappingURL=alece-carga-inicial.js.map